import { ColumnState } from 'ag-grid-community';

export const COLUMN_STATE_STORAGE_PREFIX = 'tableV3:columnState:';
// v2 discards layouts that pinned the Explanation column first.
export const COLUMN_STATE_STORAGE_VERSION = 2;

type StoredPayload = {
  version: number;
  state: ColumnState[];
};

const getStorageKey = (tableId: string): string =>
  `${COLUMN_STATE_STORAGE_PREFIX}${tableId}`;

// Derived per page, not a user choice — persisting it leaks across variants.
const userOwnedLayout = ({
  colId,
  width,
  flex,
  sort,
  sortIndex,
  pinned
}: ColumnState): ColumnState => ({
  colId,
  width,
  flex,
  sort,
  sortIndex,
  pinned
});

export const saveColumnState = (
  tableId: string,
  columnState: ColumnState[]
): void => {
  try {
    const payload: StoredPayload = {
      version: COLUMN_STATE_STORAGE_VERSION,
      state: columnState.map(userOwnedLayout)
    };
    localStorage.setItem(getStorageKey(tableId), JSON.stringify(payload));
  } catch {
    // localStorage can throw on quota-exceeded or in Safari private mode.
    // Persistence is best-effort; swallow so we never break rendering.
  }
};

export const getColumnState = (
  tableId: string,
  currentColIds?: string[]
): ColumnState[] => {
  let parsed: StoredPayload;

  try {
    const raw = localStorage.getItem(getStorageKey(tableId));
    if (!raw) {
      return [];
    }
    parsed = JSON.parse(raw) as StoredPayload;
  } catch {
    return [];
  }

  if (
    !parsed ||
    parsed.version !== COLUMN_STATE_STORAGE_VERSION ||
    !Array.isArray(parsed.state)
  ) {
    return [];
  }

  if (!currentColIds) {
    return parsed.state.map(userOwnedLayout);
  }

  // Merge guard: drop persisted entries whose colId is no longer in the
  // codebase.
  const currentColIdSet = new Set(currentColIds);
  const existingColumns = parsed.state
    .map(userOwnedLayout)
    .filter(
      (entry) => entry.colId !== undefined && currentColIdSet.has(entry.colId)
    );

  if (existingColumns.length === 0) {
    return existingColumns;
  }

  // `applyOrder` moves unlisted columns to the front, so place each new column
  // after its colDef predecessor.
  const placed = [...existingColumns];
  for (let index = 0; index < currentColIds.length; index++) {
    const colId = currentColIds[index];
    if (placed.some((entry) => entry.colId === colId)) continue;

    const insertAt =
      placed.findIndex((entry) => entry.colId === currentColIds[index - 1]) + 1;
    placed.splice(insertAt, 0, { colId });
  }

  return placed;
};

export const clearColumnState = (tableId: string): void => {
  try {
    localStorage.removeItem(getStorageKey(tableId));
  } catch {
    // Same defensive rationale as saveColumnState.
  }
};

// URL params helpers for filter state
export const getFilterStateFromURL = <T>(
  searchParams: URLSearchParams,
  tableId: string
): {
  searchFields: { [key in keyof T]: string };
  filterValues: { [key in keyof T]: { label: string; value: string }[] };
} | null => {
  // Try namespaced params first
  const searchFieldsParam = searchParams.get(`searchFields_${tableId}`);
  const filterValuesParam = searchParams.get(`filterValues_${tableId}`);

  // Fallback to non-namespaced params for backward compatibility
  const fallbackSearchFieldsParam = searchParams.get('searchFields');
  const fallbackFilterValuesParam = searchParams.get('filterValues');

  const finalSearchFieldsParam = searchFieldsParam || fallbackSearchFieldsParam;
  const finalFilterValuesParam = filterValuesParam || fallbackFilterValuesParam;

  if (!finalSearchFieldsParam && !finalFilterValuesParam) {
    return null;
  }

  try {
    return {
      searchFields: finalSearchFieldsParam
        ? JSON.parse(finalSearchFieldsParam)
        : {},
      filterValues: finalFilterValuesParam
        ? JSON.parse(finalFilterValuesParam)
        : {}
    };
  } catch {
    return null;
  }
};

/**
 * React Router's `setSearchParams` drops `location.state` by default. When a
 * table syncs its filters/pagination to the URL on mount, that silently wipes
 * the navigation state of whatever route is currently active. Pages that pass
 * data via router state (e.g. "Add jobs" reads `candidateBatchGroupId` from
 * `location.state`) then crash on a null state and, with no error boundary,
 * render a blank screen. Re-applying the router's current state (stored under
 * `history.state.usr`) keeps these URL writes side-effect-free.
 */
const currentNavigationState = (): unknown =>
  typeof window !== 'undefined' ? window.history.state?.usr : undefined;

export const saveFilterStateToURL = <T>(
  searchParams: URLSearchParams,
  setSearchParams: (
    params: URLSearchParams,
    navigateOptions?: { replace?: boolean; state?: unknown }
  ) => void,
  filters: {
    searchFields: { [key in keyof T]: string };
    filterValues: { [key in keyof T]: { label: string; value: string }[] };
  },
  tableId: string
): void => {
  const newSearchParams = new URLSearchParams(searchParams);

  const hasSearchFields = Object.keys(filters.searchFields).length > 0;
  const hasFilterValues = Object.keys(filters.filterValues).length > 0;

  // Use namespaced params
  if (hasSearchFields) {
    newSearchParams.set(
      `searchFields_${tableId}`,
      JSON.stringify(filters.searchFields)
    );
  } else {
    newSearchParams.delete(`searchFields_${tableId}`);
  }

  if (hasFilterValues) {
    newSearchParams.set(
      `filterValues_${tableId}`,
      JSON.stringify(filters.filterValues)
    );
  } else {
    newSearchParams.delete(`filterValues_${tableId}`);
  }

  setSearchParams(newSearchParams, {
    replace: true,
    state: currentNavigationState()
  });
};

export const clearFilterStateFromURL = (
  searchParams: URLSearchParams,
  setSearchParams: (
    params: URLSearchParams,
    navigateOptions?: { replace?: boolean; state?: unknown }
  ) => void,
  tableId: string
): void => {
  const newSearchParams = new URLSearchParams(searchParams);
  newSearchParams.delete(`searchFields_${tableId}`);
  newSearchParams.delete(`filterValues_${tableId}`);
  setSearchParams(newSearchParams, {
    replace: true,
    state: currentNavigationState()
  });
};

// Pagination URL helpers

// The rows-per-page menu. The explanations endpoint takes at most 100 rows.
export const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

export const getPaginationFromURL = (
  searchParams: URLSearchParams,
  tableId: string
): { page: number; pageSize: number } | null => {
  const pageParam = searchParams.get(`page_${tableId}`);
  const pageSizeParam = searchParams.get(`pageSize_${tableId}`);

  if (!pageParam && !pageSizeParam) {
    return null;
  }

  return {
    page: pageParam ? parseInt(pageParam, 10) : 0,
    // A hand-edited URL can hold a size the menu cannot show or the
    // explanations endpoint rejects.
    pageSize: DEFAULT_PAGE_SIZE_OPTIONS.includes(Number(pageSizeParam))
      ? Number(pageSizeParam)
      : DEFAULT_PAGE_SIZE
  };
};

export const savePaginationToURL = (
  searchParams: URLSearchParams,
  setSearchParams: (
    params: URLSearchParams,
    navigateOptions?: { replace?: boolean; state?: unknown }
  ) => void,
  pagination: { page: number; pageSize: number },
  tableId: string
): void => {
  const newSearchParams = new URLSearchParams(searchParams);

  // Only save non-default values to keep URL clean
  if (pagination.page > 0) {
    newSearchParams.set(`page_${tableId}`, String(pagination.page));
  } else {
    newSearchParams.delete(`page_${tableId}`);
  }

  if (pagination.pageSize !== DEFAULT_PAGE_SIZE) {
    newSearchParams.set(`pageSize_${tableId}`, String(pagination.pageSize));
  } else {
    newSearchParams.delete(`pageSize_${tableId}`);
  }

  setSearchParams(newSearchParams, {
    replace: true,
    state: currentNavigationState()
  });
};

export const clearPaginationFromURL = (
  searchParams: URLSearchParams,
  setSearchParams: (
    params: URLSearchParams,
    navigateOptions?: { replace?: boolean; state?: unknown }
  ) => void,
  tableId: string
): void => {
  const newSearchParams = new URLSearchParams(searchParams);
  newSearchParams.delete(`page_${tableId}`);
  newSearchParams.delete(`pageSize_${tableId}`);
  setSearchParams(newSearchParams, {
    replace: true,
    state: currentNavigationState()
  });
};
